import { NewsletterSection } from '@/components/home/NewsletterSection';
import { ServiceTargetSection, TargetAudience } from '@/components/prestations/ServiceTargetSection';
import { MethodologyTimeline } from '@/components/prestations/MethodologyTimeline';
import {
  ClipboardCheck,
  Database,
  GitBranch,
  Settings,
  Mail,
  Phone,
  CheckCircle,
  CheckCircle2,
  ArrowRight,
  Building2,
  Sprout,
  Briefcase,
  BarChart3,
  Lightbulb,
  FileText,
  Users,
  Search,
  Target,
  Presentation,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';

export function AuditExpertisePage() {
  const { t, i18n } = useTranslation('auditExpertise');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const services = [
    {
      title: 'Diagnostic Numérique',
      items: [
        'Cartographie complète de vos données et leur stockage',
        'Analyse des flux de données dans votre organisation',
        'Audit de la chaîne de valeur (parcelle au client final)',
        'Évaluation de votre maturité numérique',
      ],
      svg: (
        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {/* Loupe avec checklist */}
          <circle cx="40" cy="40" r="25" fill="none" stroke="white" strokeWidth="3"/>
          <line x1="58" y1="58" x2="75" y2="75" stroke="white" strokeWidth="4" strokeLinecap="round"/>
          <path d="M30 35 L35 40 L45 30" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="28" y1="45" x2="52" y2="45" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          <line x1="28" y1="50" x2="48" y2="50" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      title: 'Cartographie des Données',
      items: [
        'Identification de toutes les sources de données (capteurs, ERP, formulaires)',
        'Cartographie des échanges avec clients, fournisseurs et partenaires',
        'Évaluation de la qualité des données (fiabilité, complétude)',
        'Identification des opportunités de valorisation',
      ],
      svg: (
        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {/* Réseau de données interconnectées */}
          <circle cx="50" cy="25" r="8" fill="white"/>
          <circle cx="25" cy="50" r="8" fill="white"/>
          <circle cx="75" cy="50" r="8" fill="white"/>
          <circle cx="35" cy="75" r="8" fill="white"/>
          <circle cx="65" cy="75" r="8" fill="white"/>
          <line x1="50" y1="33" x2="25" y2="42" stroke="white" strokeWidth="2.5" opacity="0.7"/>
          <line x1="50" y1="33" x2="75" y2="42" stroke="white" strokeWidth="2.5" opacity="0.7"/>
          <line x1="25" y1="58" x2="35" y2="67" stroke="white" strokeWidth="2.5" opacity="0.7"/>
          <line x1="75" y1="58" x2="65" y2="67" stroke="white" strokeWidth="2.5" opacity="0.7"/>
          <line x1="35" y1="75" x2="65" y2="75" stroke="white" strokeWidth="2.5" opacity="0.7"/>
        </svg>
      ),
    },
    {
      title: 'Conseil Choix d\'Outils',
      items: [
        'Sélection d\'outils de collecte de données terrain (ODK, Kobo, etc.)',
        'Choix d\'architecture base de données (SQL, NoSQL, Supabase)',
        'Solutions d\'automatisation (n8n, Zapier, Make)',
        'Outils de visualisation et BI adaptés à vos besoins',
        'Benchmark comparatif des solutions du marché',
      ],
      svg: (
        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {/* Boîte à outils */}
          <rect x="20" y="40" width="60" height="35" rx="3" fill="none" stroke="white" strokeWidth="3"/>
          <path d="M20 55 L80 55" stroke="white" strokeWidth="3"/>
          <rect x="35" y="30" width="30" height="10" rx="2" fill="white" opacity="0.8"/>
          <circle cx="40" cy="65" r="4" fill="white"/>
          <circle cx="60" cy="65" r="4" fill="white"/>
          <path d="M45 62 L50 67 L58 59" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      title: 'Recommandations Stratégiques',
      items: [
        'Feuille de route numérique priorisée (impact/faisabilité)',
        'Optimisation des processus et gains d\'efficacité',
        'Mise en conformité RGPD de vos traitements',
        'Estimation budgétaire et retours sur investissement',
      ],
      svg: (
        <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {/* Ampoule avec check */}
          <circle cx="50" cy="55" r="20" fill="none" stroke="white" strokeWidth="3"/>
          <path d="M42 70 L42 80 L58 80 L58 70" stroke="white" strokeWidth="3" strokeLinecap="round"/>
          <rect x="44" y="80" width="12" height="4" rx="2" fill="white"/>
          <path d="M50 35 L50 42" stroke="white" strokeWidth="3" strokeLinecap="round"/>
          <path d="M42 48 L48 54 L60 42" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  const methodologySteps = [
    {
      number: '1',
      title: 'Cadrage',
      description: 'Entretiens avec les parties prenantes (direction, métiers, IT), compréhension de votre contexte, enjeux et objectifs. Définition du périmètre de l\'audit. (1-2 jours)',
      icon: Users,
    },
    {
      number: '2',
      title: 'Diagnostic',
      description: 'Cartographie exhaustive des données et flux, analyse de vos outils actuels (points forts, limites), identification des irritants et opportunités, benchmark des solutions du marché. (3-5 jours)',
      icon: Search,
    },
    {
      number: '3',
      title: 'Recommandations',
      description: 'Synthèse des constats, propositions d\'amélioration priorisées, feuille de route (court, moyen, long terme) et estimation budgétaire. (2-3 jours)',
      icon: Target,
    },
    {
      number: '4',
      title: 'Présentation & Accompagnement',
      description: 'Restitution des résultats (présentation visio/présentiel), rapport détaillé avec schémas et recommandations, accompagnement post-audit optionnel (aide au choix, mise en œuvre). (1 jour + suivi)',
      icon: Presentation,
    },
  ];

  const targetAudiences: TargetAudience[] = [
    {
      icon: Building2,
      title: "Coopératives & Instituts",
      description: "Auditez votre écosystème numérique, cartographiez vos flux de données et optimisez votre stack d'outils pour améliorer l'accompagnement de vos adhérents",
      badge: "25+ structures auditées",
    },
    {
      icon: Sprout,
      title: "Chambres d'Agriculture",
      description: "Évaluez votre maturité numérique, identifiez les outils pertinents pour vos conseillers et construisez votre stratégie de transformation digitale",
      badge: "10+ chambres accompagnées",
    },
    {
      icon: Briefcase,
      title: "Entreprises Agri-tech",
      description: "Positionnez vos solutions dans l'écosystème, identifiez vos concurrents et comprenez les flux de données de vos clients pour affiner votre stratégie produit",
      badge: "15+ entreprises conseillées",
    },
  ];
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0D974E] to-[#0a7a3f] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="text-sm mb-6 opacity-90">
              <a href={`/${currentLocale}`} className="hover:underline">
                {t('breadcrumb.home')}
              </a>
              {' > '}
              <a href={`/${currentLocale}/prestations`} className="hover:underline">
                {t('breadcrumb.prestations')}
              </a>
              {' > '}
              <span>{t('breadcrumb.current')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Texte */}
              <div>
                <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-6">
                  {t('hero.title')}
                </h1>
                <p className="text-xl mb-8 opacity-95">
                  {t('hero.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`/${currentLocale}/contact`}
                    className="px-8 py-4 bg-white text-[#0D974E] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg text-center"
                  >
                    {t('hero.ctaPrimary')}
                  </a>
                  <a
                    href="#services"
                    className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center"
                  >
                    {t('hero.ctaSecondary')}
                  </a>
                </div>
              </div>

              {/* Visuel */}
              <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Database className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Cartographie données</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <GitBranch className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Analyse flux</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Settings className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Choix outils</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <ClipboardCheck className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Recommandations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <ServiceTargetSection
        title="Pour qui ?"
        subtitle="J'accompagne les structures agricoles dans leur transformation numérique"
        audiences={targetAudiences}
        accentColor="green"
      />

      {/* Services proposés */}
      <section id="services" className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black mb-6 text-center">
              Mes services en détail
            </h2>
            <p className="text-center text-aspexit-gray mb-16 max-w-2xl mx-auto">
              Un accompagnement sur-mesure pour comprendre vos enjeux
              numériques et optimiser votre organisation
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-aspexit-gray-light"
                  >
                    {/* SVG Icon + Title */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-[#0D974E] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md p-3">
                        {service.svg}
                      </div>
                      <h3 className="text-2xl font-heading font-bold text-aspexit-black">
                        {service.title}
                      </h3>
                    </div>

                    {/* Items */}
                    <ul className="space-y-3">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-[#0D974E] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-aspexit-gray leading-relaxed">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Méthodologie */}
      <MethodologyTimeline
        title="Comment je travaille"
        subtitle="Ma méthode en 4 phases"
        steps={methodologySteps}
        size="compact"
        accentColor="green"
      />

      {/* Pourquoi moi */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-12 text-center">
              Pourquoi travailler avec moi
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#0D974E] mb-2">
                  10+
                </div>
                <p className="text-aspexit-gray">
                  Années d'expérience numérique agricole
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#0D974E] mb-2">
                  60+
                </div>
                <p className="text-aspexit-gray">
                  Dossiers techniques publiés
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#0D974E] mb-2">
                  100%
                </div>
                <p className="text-aspexit-gray">Conseil indépendant</p>
              </div>
            </div>

            <div className="bg-aspexit-gray-light rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-4">
                Mes atouts
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-aspexit-gray">
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Double expertise : Agronomie + Data Science
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Compréhension terrain (10+ ans)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Vision systémique (données + processus + humains)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Conseil objectif (aucune solution propriétaire)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Partenaire Binaree (transformation digitale)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#0D974E]">✓</span>
                  Co-créateur Wiki Agri Tech
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#0D974E] to-[#0a7a3f] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Un projet d'audit ou de conseil numérique ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Discutons de vos besoins et trouvons la solution adaptée
            </p>

            <div className="flex justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-white text-[#0D974E] rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Me contacter
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
